import { computed, effect, inject, Injector, untracked } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { finalize, switchMap } from 'rxjs';

import { Account } from '@zeusm/personal/core/entities';
import { PersonalShellStore } from '@zeusm/personal/shell';
import { DictionariesStore } from '@zeusm/personal/store';
import { GlobalDialogService } from '@zeusm/shared/core/services';
import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@zeusm/shared/store';

import { CARDS_API } from './api/cards-api.token';
import { InfoDialogStatus } from './components/create-card/create-card-info/create-card-info.component';
import { CardsErrorDialogComponent } from './components/error-dialog/error-dialog.component';
import { CardCloseReason, CardProduct, ConfirmOrderData, CreateCardReq, ReissueCardReq } from './entities/cards';
import { CardActionSyncAction, CardActionSyncService, CardActionSyncState } from './services/card-action-sync.service';

interface CardsState {
  products: CardProduct[];
  createdCard: Account;
  createCardData: ConfirmOrderData;
}

const initialState: CardsState = {
  products: [],
  createdCard: null,
  createCardData: null,
};

export const CardsStore = signalStore(
  withDevtools('cards'),
  withState(initialState),
  withRequestStatus(['getProducts', 'getProduct', 'createCard', 'getCardDetails', 'reissueCard', 'blockCard', 'unblockCard', 'closeCard']),
  withRequestHandler(),
  withProps(store => ({
    store,
    shellStore: inject(PersonalShellStore),
    cardsApi: inject(CARDS_API),
    spinnerService: inject(SpinnerService),
    dictionariesStore: inject(DictionariesStore),
    dialogService: inject(GlobalDialogService),
    injector: inject(Injector),
    storeHelperService: inject(StoreHelperService),
    cardActionSyncService: inject(CardActionSyncService),
  })),
  withComputed(({ store }) => ({
    isCreateSuccess: computed(() => store.isCreateCardSuccess()),
    createError: computed(() => store.createCardError()),
    status: computed<InfoDialogStatus>(() => {
      if (store.isCreateCardSuccess() || store.isReissueCardSuccess()) {
        return 'succeeded';
      }
      if (store.createCardError() || store.reissueCardError()) {
        return 'failed';
      }
      return 'pending';
    }),
    createCardErrorText: computed<string>(() => {
      if (!store.createCardError()) {
        return '';
      }
      return store.createCardError().originalError?.error?.message;
    }),
    reissueCardErrorText: computed<string>(() => {
      if (!store.reissueCardError()) {
        return '';
      }
      return store.reissueCardError().originalError?.error?.message;
    }),
    cardActionErrorText: computed<string>(() => {
      const blockError = store.blockCardError();
      if (blockError) {
        const error = blockError.originalError?.error;
        return error?.message || error?.details || '';
      }

      const unblockError = store.unblockCardError();
      if (unblockError) {
        return unblockError.originalError?.error?.message || '';
      }

      const closeError = store.closeCardError();
      if (closeError) {
        const error = closeError.originalError?.error;
        return error?.message || error?.details || '';
      }

      return '';
    }),
    isCardActionSuccess: computed(() => store.isBlockCardSuccess() || store.isUnblockCardSuccess() || store.isCloseCardSuccess()),
  })),
  withMethods(store => ({
    selectProductById: (id: string) => computed(() => store.products().find(product => product.id === id)),
    setCreateCardData: (data: Partial<ConfirmOrderData>) => {
      patchState(store, {
        createCardData: {
          ...store.createCardData(),
          ...data,
        },
      });
    },
    clearCreatedCardData: () => {
      patchState(store, {
        createCardData: null,
        createdCard: null,
      });
    },
    openFailedDialog(errorText: string): void {
      if (!errorText) return;
      store.dialogService
        .open<CardsErrorDialogComponent, { errorText: string }>(
          {
            components: [CardsErrorDialogComponent],
            data: { errorText },
          },
          store.injector,
        )
        .afterClosed()
        .subscribe();
    },
  })),
  withMethods(({ store, cardsApi, cardActionSyncService }) => {
    const syncAction = (cardId: string, action: CardActionSyncAction, state: CardActionSyncState) => {
      if (!cardId) {
        return;
      }
      cardActionSyncService.emit({ cardId, action, state });
    };

    return {
      getProducts: rxMethod<void>(
        switchMap(() => {
          patchState(store, setPendingStatusFor('getProducts'));
          return cardsApi.getProducts().pipe(
            tapResponse({
              next: res => {
                patchState(store, { products: res.cardProducts }, setSuccessStatusFor('getProducts'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('getProducts', error)),
            }),
          );
        }),
      ),
      getProduct: rxMethod<string>(
        switchMap(id => {
          patchState(store, setPendingStatusFor('getProduct'));
          return cardsApi.getProduct(id).pipe(
            tapResponse({
              next: product => {
                patchState(store, { products: [product] }, setSuccessStatusFor('getProduct'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('getProduct', error)),
            }),
          );
        }),
      ),
      createCard: rxMethod<CreateCardReq>(
        switchMap(params => {
          patchState(store, setPendingStatusFor('createCard'));
          return cardsApi.create(params).pipe(
            tapResponse({
              next: createdCard => {
                patchState(store, { createdCard }, setSuccessStatusFor('createCard'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('createCard', error)),
            }),
          );
        }),
      ),
      reissueCard: rxMethod<ReissueCardReq>(
        switchMap(params => {
          patchState(store, setPendingStatusFor('reissueCard'));
          syncAction(params.cardId, 'reissueCard', 'started');
          return cardsApi.reissueCard(params).pipe(
            tapResponse({
              next: createdCard => {
                patchState(store, { createdCard }, setSuccessStatusFor('reissueCard'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('reissueCard', error)),
            }),
            finalize(() => syncAction(params.cardId, 'reissueCard', 'finished')),
          );
        }),
      ),
      blockCard: rxMethod<string>(
        switchMap(id => {
          patchState(store, setPendingStatusFor('blockCard'));
          syncAction(id, 'blockCard', 'started');
          return cardsApi.blockCard(id).pipe(
            tapResponse({
              next: updatedCard => {
                patchState(store, { createdCard: updatedCard }, setSuccessStatusFor('blockCard'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('blockCard', error)),
            }),
            finalize(() => syncAction(id, 'blockCard', 'finished')),
          );
        }),
      ),
      unblockCard: rxMethod<string>(
        switchMap(id => {
          patchState(store, setPendingStatusFor('unblockCard'));
          syncAction(id, 'unblockCard', 'started');
          return cardsApi.unblockCard(id).pipe(
            tapResponse({
              next: updatedCard => {
                patchState(store, { createdCard: updatedCard }, setSuccessStatusFor('unblockCard'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('unblockCard', error)),
            }),
            finalize(() => syncAction(id, 'unblockCard', 'finished')),
          );
        }),
      ),
      closeCard: rxMethod<{
        id: string;
        reason?: CardCloseReason;
        otherReason?: string;
        targetAccountID?: string;
      }>(
        switchMap(({ id, reason, otherReason, targetAccountID }) => {
          patchState(store, setPendingStatusFor('closeCard'));
          syncAction(id, 'closeCard', 'started');
          return cardsApi.closeCard(id, reason, otherReason, targetAccountID).pipe(
            tapResponse({
              next: updatedCard => {
                patchState(store, { createdCard: updatedCard }, setSuccessStatusFor('closeCard'));
              },
              error: (error: AppError) => patchState(store, setErrorStatusFor('closeCard', error)),
            }),
            finalize(() => syncAction(id, 'closeCard', 'finished')),
          );
        }),
      ),
    };
  }),
  withHooks({
    onInit(store) {
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });

      effect(() => {
        if (store.isCardActionSuccess()) {
          untracked(() => store.shellStore.getAccounts());
        }
      });

      effect(() => {
        const errorText = store.cardActionErrorText();
        if (errorText) {
          untracked(() => store.openFailedDialog(errorText));
        }
      });

      const unsubscribe = store.cardActionSyncService.onMessage(event => {
        if (event.state !== 'finished' || store.shellStore.isGetAccountsPending?.()) {
          return;
        }

        untracked(() => store.shellStore.getAccounts());
      });

      return () => unsubscribe();
    },
  }),
);
